/**
 * Player-facing round HUD: phase, countdown, your team, score, the
 * hunter/hider split, and the "you can't move right now" banner.
 *
 * Deliberately separate from DebugOverlay (debug/overlay.ts), which an e2e
 * harness parses by exact rendered text and must not change shape. This
 * panel has no such contract and is free to evolve with the game's UI.
 *
 * Renders every frame from the current `RoundState` and server tick — it
 * never stores a decaying countdown itself, matching the reasoning in
 * round.ts: the deadline is a pure function of `tick`, not a value to tick
 * down on a timer.
 */

import {
  phaseLabel,
  secondsRemaining,
  teamLabel,
  type RoundState,
  type TeamId,
} from "@prop-hunt/shared";
import { countTeams, heldMessage } from "./round.js";

export interface RoundHudStats {
  /**
   * The round as of `serverTick` — `roundStateAtTick`, not the raw state the
   * newest Msg.Round carried.
   *
   * It must be the derived one, because `heldMessage` delegates to `canMove`
   * and the movement gate now derives its phase the same way. Reading the raw
   * state here would leave a released hunter moving freely underneath a HELD
   * banner for ~RTT/2 — the gate fix showing up as a UI bug.
   */
  round: RoundState;
  /**
   * Current server tick, **unwrapped** — `Connection.estimatedServerTick`, not
   * `Connection.serverTick`.
   *
   * Those two used to be interchangeable here: `phaseStartedTick` was sent as
   * low 16 bits precisely so it could be subtracted from the snapshot's wrapped
   * tick. It is a u32 now, because the movement gate derives the phase from it
   * and a wrap there is a desync rather than a cosmetic glitch. Handing this
   * field a wrapped tick would put the countdown out by up to 65536 ticks.
   */
  serverTick: number;
  myTeam: TeamId;
  teams: ReadonlyMap<number, TeamId>;
  /** Everyone found so far, so the hider count is survivors rather than roster. */
  found: ReadonlySet<number>;
  /** Whether the local player has been found. See `heldMessage`. */
  amFound: boolean;
}

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export class RoundHud {
  private readonly phaseEl: HTMLElement;
  private readonly countdownEl: HTMLElement;
  private readonly scoreEl: HTMLElement;
  private readonly teamEl: HTMLElement;
  private readonly countsEl: HTMLElement;
  private readonly heldEl: HTMLElement;

  constructor(root: HTMLElement) {
    root.innerHTML = "";

    const panel = document.createElement("div");
    panel.className = "round-panel";

    const header = document.createElement("div");
    header.className = "round-header";
    this.phaseEl = document.createElement("span");
    this.phaseEl.className = "round-phase";
    this.countdownEl = document.createElement("span");
    this.countdownEl.className = "round-countdown";
    header.append(this.phaseEl, this.countdownEl);

    this.scoreEl = document.createElement("div");
    this.scoreEl.className = "round-line";
    this.teamEl = document.createElement("div");
    this.teamEl.className = "round-line round-team";
    this.countsEl = document.createElement("div");
    this.countsEl.className = "round-line";

    panel.append(header, this.scoreEl, this.teamEl, this.countsEl);
    root.appendChild(panel);

    this.heldEl = document.createElement("div");
    this.heldEl.className = "held-banner";
    this.heldEl.hidden = true;
    root.appendChild(this.heldEl);
  }

  update(stats: RoundHudStats): void {
    const seconds = secondsRemaining(stats.round, stats.serverTick);

    this.phaseEl.textContent = phaseLabel(stats.round.phase).toUpperCase();
    this.countdownEl.textContent = seconds === null ? "" : formatClock(seconds);

    this.scoreEl.textContent =
      `round ${stats.round.roundNumber}` +
      `  ·  hunters ${stats.round.huntersWon}–${stats.round.hidersWon} hiders`;

    this.teamEl.textContent = `you: ${teamLabel(stats.myTeam)}`;
    this.teamEl.dataset.team = teamLabel(stats.myTeam);

    const counts = countTeams(stats.teams, stats.found);
    // "left" rather than a bare count: this is the number that ends the round,
    // and a hider watching it fall should know what it is counting.
    this.countsEl.textContent =
      `hunters ${counts.hunters}  ·  ${counts.hiders} hiders left` +
      (counts.hidersFound > 0 ? `  ·  ${counts.hidersFound} found` : "");

    const held = heldMessage(stats.round.phase, stats.myTeam, seconds, stats.amFound);
    this.heldEl.hidden = held === null;
    if (held !== null) this.heldEl.textContent = held;
  }
}
